import { useEffect } from 'react';
import { BrowserRouter } from 'react-router-dom';
import { AppRoutes } from './router';
import { I18nextProvider } from 'react-i18next';
import i18n from './i18n';
import { AuthProvider } from './context/AuthContext';
import AppStateSyncProvider from './context/AppStateSyncProvider';
import ScrollToTop from './components/base/ScrollToTop';
import { SiteModeProvider } from './context/SiteModeContext';

function App() {
  useEffect(() => {
    const chunkReloadKey = 'zobu_chunk_reload_attempted';
    sessionStorage.removeItem(chunkReloadKey);

    const handleChunkLoadFailure = (message: string) => {
      if (!/Failed to fetch dynamically imported module|Importing a module script failed|Loading chunk/i.test(message)) {
        return;
      }

      if (sessionStorage.getItem(chunkReloadKey) === '1') {
        return;
      }

      sessionStorage.setItem(chunkReloadKey, '1');
      window.location.reload();
    };

    const onUnhandledRejection = (event: PromiseRejectionEvent) => {
      const reason = event.reason;
      const message = reason instanceof Error ? reason.message : String(reason ?? '');
      handleChunkLoadFailure(message);
    };

    const onError = (event: ErrorEvent) => {
      handleChunkLoadFailure(event.message);
    };

    window.addEventListener('unhandledrejection', onUnhandledRejection);
    window.addEventListener('error', onError);

    return () => {
      window.removeEventListener('unhandledrejection', onUnhandledRejection);
      window.removeEventListener('error', onError);
    };
  }, []);

  return (
    <I18nextProvider i18n={i18n}>
      <AuthProvider>
        <AppStateSyncProvider>
          <SiteModeProvider>
            <BrowserRouter basename={__BASE_PATH__}>
              <ScrollToTop />
              <AppRoutes />
            </BrowserRouter>
          </SiteModeProvider>
        </AppStateSyncProvider>
      </AuthProvider>
    </I18nextProvider>
  );
}

export default App;
